/**
 * @file PlanHistoryModal.js
 * @version 2.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-30
 * @description Plan History: one delivery day's transport plan, everything that
 *              happened to it — board actions (lock, order of loads, times,
 *              door layouts, board moves), changes to the day's orders and
 *              POs, date moves on and off the day, and every email sent
 *              (paperwork, mills, office, POs), each viewable as sent.
 *              Served by GET /api/transport/planning/history/:date
 *              (services/planHistoryService.js). Covers both boards.
 *
 *              2.0.0 (2026-10-30, Cam — "change the history modal in transport
 *              planning components to have tabs not filters: Emails / Order
 *              Amendments / Planning — in that order (defaulting to emails)
 *              and then group by date, today's top, rest collapsed by default,
 *              and sub group by event type and order by time, all subgroups
 *              collapsed by default"):
 *                - TABS in place of the Show dropdown — Emails (opens on it,
 *                  or on `initialTab`), Order Amendments, Planning — each with
 *                  its count; Emails shows a red triangle when any email in it
 *                  bounced, was marked as spam, was rejected or failed
 *                  (utils/emailDelivery). The search box stays, at the right
 *                  of the tabs, and searches within the tab (the counts follow
 *                  it); a search opens every group it matches.
 *                - DAYS: one per London day, newest first; today's open, the
 *                  rest closed.
 *                - SUBGROUPS within a day, newest first, all closed: an email
 *                  send (one driver paperwork or mill send, one office
 *                  summary — the server's `send` key), else a run of the same
 *                  type of change (or email kind) by the same person, each
 *                  within 2 minutes of the run's latest, whatever came in
 *                  between (utils/planHistoryGroups 1.1.0). Heading: what, the time of its
 *                  first event to the second ("Mill order summary emailed
 *                  (14:32:25)"), how many, who, and the triangle when an email
 *                  in it did not arrive. Open, it lists the entries as before;
 *                  an email that did not arrive names the addresses, and why.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import SearchHelp from './SearchHelp';
import LoadingSpinner from './LoadingSpinner';
import SentEmailModal from './SentEmailModal';
import { API_URL } from '../config';
import { matchesAllTerms } from '../utils/searchMatch';
import { deliveryStatus } from '../utils/emailDelivery';
import {
  PLAN_HISTORY_TABS, londonDay, longDayLabel, dayGroupsOf, eventHasProblem, countWords, isDayOpen, isSubgroupOpen
} from '../utils/planHistoryGroups';
import { renderAuditChanges, getVisibleChanges, getActionInfo, getEntityTypeLabel, formatFieldName, entrySource } from './AuditHistoryModal';
import './AuditHistoryModal.css';
import './PlanHistoryModal.css';

const KIND_ICON = { plan: 'route', order: 'receipt_long', email: 'mail' };
const KIND_WORD = { plan: 'Planning', order: 'Order amendment', email: 'Email' };

const timeOf = (d) => new Date(d).toLocaleTimeString('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});
const shortDate = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const capitalise = (s) => String(s || '').replace(/^./, ch => ch.toUpperCase());

/*
 * The one-line headline for an event. Board actions have their own names;
 * a date move reads as a move relative to THIS day; everything else uses the
 * audit log's own action label, so it reads as it does in record history.
 */
function headline(ev, date) {
  const c = ev.changes || {};
  switch (ev.action) {
    case 'DAY_LOCKED': return c._auto ? 'Day locked automatically, on first opening it after the day' : 'Day locked';
    case 'DAY_UNLOCKED': return 'Day unlocked';
    case 'RESEQUENCED': return 'Loads reordered';
    case 'TIME_SET': return 'Collection time set';
    case 'RUN_COMBINED': return 'Rows combined into one run';
    case 'RUN_SPLIT': return 'Rows split out of their run';
    case 'DOOR_LAYOUT': return 'Door layout changed';
    // Loads and Drops (scrapped 2026-10-12): kept so its history still reads.
    case 'DAY_PLAN_CHANGED': return `Day plan changed${c._lorry ? ` — ${c._lorry}` : ''}${c._changed ? ` (${c._changed})` : ''}`;
    // Lorry day planning: one lorry's day saved from its window (`day` holds
    // the day before and after), a drop time or a planner's note typed on the board.
    case 'LORRY_DAY_CHANGED': return `Lorry day changed${c._lorry ? ` — ${c._lorry}` : ''}${c._changed ? ` (${c._changed})` : ''}`;
    case 'DROP_TIME_SET': return 'Drop time set';
    // Who carried it, corrected after the day from the PO editor or Add Supplied Qty.
    case 'TRANSPORT_CORRECTED': return `Who delivered it corrected${c._source ? ` — ${c._source}` : ''}`;
    case 'PLAN_NOTE_SET': return 'Planner’s note changed';
    // Lorry day planning switched off with "revert" (services/transportBaselineService),
    // a day's plans set from its sent paperwork, an old-rule day's snapshot put right.
    case 'PLAN_REVERTED': return `Planning put back as it was before lorry day planning was switched on${c._changed ? ` (${c._changed})` : ''}`;
    case 'LORRY_DAY_SEEDED': return `Lorry day plans set to match the paperwork that went out${c._changed ? ` (${c._changed})` : ''}`;
    case 'SNAPSHOT_OLD_RULES': return 'Day kept as planned before lorry day planning';
    case 'LINE_PLAN_CHANGED': return `Plan changed${c._changed ? ` (${c._changed})` : ''}`;
    case 'BOARD_MOVE': return `Moved to the ${c.board?.new === 'bag' ? 'Haulage/Bag' : 'Bulk'} board`;
    case 'UNKNOWN_PAYLOAD': return 'Unknown-vehicle payload set';
    // One planner at a time: a request not answered in 8 seconds (services/planningControlService).
    // An outside haulier chosen on the board, and the order's PO-H raised there
    // (services/outsideHaulierPohService).
    case 'HAULAGE_PO_RAISED': return c._summary || `${c.purchase_order || 'PO-H'} raised to ${c.supplier || 'the haulier'} for ${c.customer_order || 'the order'}${c.haulier ? ` (${c.haulier} chosen on the board)` : ''}`;
    case 'PLANNING_CONTROL_TAKEN': return `Took over planning ${c.board || 'the board'} from ${c.from || 'the planner'} — no answer in 8 seconds`;
    case 'SNAPSHOT': return 'Day closed — plan snapshot taken';
    case 'ACKNOWLEDGED': return 'Changes acknowledged';
    case 'PLAN_UPDATE': return 'Haulage job updated';
    // From a board's "Archive order…" / "Archive PO…", or a search screen.
    case 'ARCHIVE':
      if (ev.entityType === 'customer_order') {
        const pos = Array.isArray(c.archived_pos) ? c.archived_pos : [];
        return `Order archived — off the boards${pos.length ? `, with ${pos.join(', ')}` : ''}`;
      }
      return ev.entityType === 'purchase_order' ? 'PO archived — off its order and the boards' : 'Archived';
    case 'EMAIL_SENT': {
      const label = ev.email?.label || ev.emailLabel;
      return label ? `${label} emailed` : 'Email sent';
    }
    default: break;
  }
  const move = c.delivery_date;
  if (move && typeof move === 'object') {
    const from = String(move.old || '').slice(0, 10);
    const to = String(move.new || '').slice(0, 10);
    if (from === date && to !== date) return `Moved off this day, to ${shortDate(to)}`;
    if (to === date && from !== date) return `Moved onto this day${from ? `, from ${shortDate(from)}` : ''}`;
  }
  // An edit reads better as what it changed: "Haulier changed".
  if (['UPDATE', 'PO_UPDATE', 'LINE_UPDATE'].includes(ev.action)) {
    const names = Object.keys(getVisibleChanges(c)).map(formatFieldName);
    if (names.length && names.length <= 3) {
      return capitalise(`${names.join(', ')} changed`);
    }
  }
  return getActionInfo(ev.action).label;
}

/*
 * What a subgroup of several events was, without the parts about one record
 * (one event reads as its own headline).
 */
const GROUP_WORDS = {
  ARCHIVE: 'Archived',
  DAY_PLAN_CHANGED: 'Day plan changed',
  LORRY_DAY_CHANGED: 'Lorry day changed',
  TRANSPORT_CORRECTED: 'Who delivered it corrected',
  PLAN_REVERTED: 'Planning put back as it was before lorry day planning',
  LORRY_DAY_SEEDED: 'Lorry day plans set to match the paperwork that went out',
  LINE_PLAN_CHANGED: 'Plan changed',
  BOARD_MOVE: 'Moved between the boards',
  HAULAGE_PO_RAISED: 'PO-H raised for an outside haulier',
  PLANNING_CONTROL_TAKEN: 'Took over planning'
};

function subgroupLabel(sub, date) {
  const [kind, ...rest] = String(sub.typeKey || '').split(':');
  const detail = rest.join(':');
  if (kind === 'email') return detail ? `${detail} emailed` : 'Email sent';
  if (sub.count === 1) return headline(sub.sample, date);
  if (kind === 'move') return detail === 'off' ? 'Moved off this day' : 'Moved onto this day';
  if (kind === 'edit') {
    const names = detail.split(',').filter(Boolean).map(formatFieldName);
    if (names.length <= 3) return capitalise(`${names.join(', ')} changed`);
    return capitalise(`${names.slice(0, 2).join(', ')} and ${names.length - 2} more fields changed`);
  }
  return GROUP_WORDS[sub.sample?.action] || headline(sub.sample, date);
}

/** The records a subgroup touched, in the order it touched them: "SO-D10673, PO-D10641 + 3 more". */
function subgroupRecords(sub) {
  const refs = [...new Set([...sub.events].reverse()
    .filter(ev => ev.identifier && ev.entityType !== 'transport_day' && ev.action !== 'SNAPSHOT')
    .map(ev => ev.identifier))];
  if (!refs.length) return '';
  return refs.length <= 3 ? refs.join(', ') : `${refs.slice(0, 2).join(', ')} + ${refs.length - 2} more`;
}

/** The underscore context fields, and where it was done, as a short note. */
function contextNote(ev) {
  const c = ev.changes || {};
  const where = entrySource({ source: ev.source, changes: c });
  return [c._so, c._product, c._po, c._driver && `Driver: ${c._driver}`, c._rows, where && `via ${where}`]
    .filter(Boolean).join(' · ');
}

/** Searchable text for an event — kept in step with SearchHelp's list below. */
function searchText(ev) {
  const c = ev.changes || {};
  return [
    ev.identifier, ev.user, headline(ev, ''), contextNote(ev),
    c.haulier?.new, c.haulier?.old, c.driver?.new, c.driver?.old, c.vehicle?.new, c.vehicle?.old,
    ev.email?.subject, ...(ev.email?.to || [])
  ];
}

/** Which addresses an email did not reach, and why — in words. */
function problemText(email) {
  const list = email?.problemRecipients || [];
  if (!list.length) return email?.error || '';
  return list.map(r => `${r.address} — ${deliveryStatus(r.status).label}${r.reason ? `: ${r.reason}` : ''}`).join('\n');
}

function Warn({ count, where }) {
  const words = `${count} ${count === 1 ? 'email' : 'emails'} not delivered${where ? ` ${where}` : ''} — bounced, marked as spam, rejected or failed to send`;
  return (
    <span className="material-symbols-rounded ph-warn" role="img" aria-label={words} title={words}>warning</span>
  );
}

function PlanHistoryModal({ isOpen, onClose, date, initialTab = 'email', today: todayProp }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(initialTab || 'email');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  // Days and subgroups the person has opened or closed against their default.
  const [flippedDays, setFlippedDays] = useState(() => new Set());
  const [flippedSubs, setFlippedSubs] = useState(() => new Set());
  const [viewEmail, setViewEmail] = useState(null);

  useEffect(() => {
    if (!isOpen || !date) return undefined;
    let live = true;
    setData(null);
    setError(null);
    setExpanded(new Set());
    setFlippedDays(new Set());
    setFlippedSubs(new Set());
    fetch(`${API_URL}/api/transport/planning/history/${date}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!live) return;
        if (!ok || d.success === false) throw new Error(d.error || 'Failed to load plan history');
        setData(d);
      })
      .catch(e => live && setError(e.message));
    return () => { live = false; };
  }, [isOpen, date]);

  // Every opening starts on the tab asked for (Emails unless told otherwise).
  useEffect(() => { if (isOpen) setTab(PLAN_HISTORY_TABS.some(t => t.key === initialTab) ? initialTab : 'email'); }, [isOpen, initialTab]);

  // A new search opens what it finds; the person's own opening and closing
  // starts again from there.
  useEffect(() => {
    setFlippedDays(new Set());
    setFlippedSubs(new Set());
  }, [search]);

  const today = todayProp || londonDay(new Date());
  const searching = search.trim() !== '';

  const matching = useMemo(
    () => (data?.events || []).filter(ev => matchesAllTerms(searchText(ev), search)),
    [data, search]
  );

  const counts = useMemo(() => {
    const n = { email: 0, order: 0, plan: 0 };
    for (const ev of matching) if (ev.kind in n) n[ev.kind] += 1;
    return n;
  }, [matching]);

  // The tab's triangle: any email of the day that did not arrive, whatever the search.
  const emailProblems = useMemo(() => (data?.events || []).filter(eventHasProblem).length, [data]);

  const days = useMemo(
    () => dayGroupsOf(matching.filter(ev => ev.kind === tab), { date }),
    [matching, tab, date]
  );

  const flip = (setter, key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ← → (and Home / End) move between the tabs, as a tab list should.
  const onTabKey = (e) => {
    const i = PLAN_HISTORY_TABS.findIndex(t => t.key === tab);
    const last = PLAN_HISTORY_TABS.length - 1;
    const to = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    setTab(PLAN_HISTORY_TABS[to].key);
    const el = document.getElementById(`ph-tab-${PLAN_HISTORY_TABS[to].key}`);
    if (el) el.focus();
  };

  const renderEntry = (ev, i, list) => {
    const open = expanded.has(ev.key);
    const fields = ev.kind === 'email' || !ev.changes ? 0
      : Object.keys(getVisibleChanges(ev.changes)).length;
    const expandable = fields > 0 && !['CREATE', 'DELETE'].includes(ev.action);
    const canView = !!(ev.emailId || ev.changeLogId);
    const st = deliveryStatus(ev.email?.status);
    const note = contextNote(ev);
    const failedTo = eventHasProblem(ev) ? (ev.email.problemRecipients || []) : [];
    return (
      <div key={ev.key} className={`timeline-entry ph-entry ph-kind-${ev.kind} ${open ? 'expanded' : ''}`}>
        <div className="timeline-marker">
          <div className="marker-dot"></div>
          {i < list.length - 1 && <div className="marker-line"></div>}
        </div>
        <div className="timeline-content">
          <div
            className={`entry-header ${expandable ? 'ph-clickable' : ''}`}
            onClick={() => expandable && flip(setExpanded, ev.key)}
          >
            <span className="ph-time">{timeOf(ev.at)}</span>
            <span className="material-symbols-rounded ph-kind" title={KIND_WORD[ev.kind]}>
              {KIND_ICON[ev.kind]}
            </span>
            <div className="ph-what">
              <div className="ph-title">
                <span className="ph-headline">{headline(ev, date)}</span>
                {ev.identifier && ev.entityType !== 'transport_day' && ev.action !== 'SNAPSHOT' && (
                  <span className="ph-ident" title={ev.entityType ? getEntityTypeLabel(ev.entityType) : ''}>{ev.identifier}</span>
                )}
              </div>
              {note && <div className="ph-note">{note}</div>}
              {ev.email && (
                <div className="ph-email">
                  <span className="ph-email-to">{ev.email.to.join(', ') || '—'}</span>
                  <span className="ph-email-subject">{ev.email.subject}</span>
                  <span className={`ph-delivery ${st.tone}`} title={problemText(ev.email)}>
                    <span className="material-symbols-rounded">{st.icon}</span>{st.label}
                  </span>
                  {ev.email.attachments > 0 && (
                    <span className="ph-attach">
                      <span className="material-symbols-rounded">attach_file</span>
                      {ev.email.attachments}
                    </span>
                  )}
                </div>
              )}
              {failedTo.length > 0 && (
                <ul className="ph-email-problems">
                  {failedTo.map(r => (
                    <li key={r.address}>
                      <span className="material-symbols-rounded ph-warn" aria-hidden="true">warning</span>
                      <span>
                        <strong>{r.address}</strong> {deliveryStatus(r.status).verb || deliveryStatus(r.status).label.toLowerCase()}
                        {r.reason ? <span className="ph-problem-reason"> — {r.reason}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="entry-user">
              <span className="material-symbols-rounded">person</span>
              <span>{ev.user}</span>
            </div>
            <button
              type="button"
              className={`btn-icon entry-action${canView ? '' : ' btn-disabled'}`}
              disabled={!canView}
              title={canView ? 'View the email as it was sent' : 'Not an email'}
              onClick={(e) => {
                e.stopPropagation();
                if (canView) setViewEmail(ev.emailId ? { emailId: ev.emailId } : { changeLogId: ev.changeLogId });
              }}
            >
              <span className="material-symbols-rounded">mail</span>
            </button>
            <span className={`material-symbols-rounded ph-expand ${expandable ? '' : 'ph-hidden'}`}>expand_more</span>
          </div>
          {open && (
            <div className="entry-changes">
              {renderAuditChanges(ev.changes, ev.action, ev.resolved)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabInfo = PLAN_HISTORY_TABS.find(t => t.key === tab) || PLAN_HISTORY_TABS[0];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={date ? `Plan History · ${longDayLabel(date)}` : 'Plan History'}
      size="xl"
      className="plan-history audit-history-modal"
    >
      {error && <p className="ph-error">{error}</p>}
      {!data && !error && <LoadingSpinner />}

      {data && (
        <>
          <div className="ph-summary">
            <span><strong>{data.counts.orders}</strong> order{data.counts.orders === 1 ? '' : 's'}</span>
            <span><strong>{data.counts.purchaseOrders}</strong> PO{data.counts.purchaseOrders === 1 ? '' : 's'}</span>
            {data.counts.haulageJobs > 0 && <span><strong>{data.counts.haulageJobs}</strong> haulage job{data.counts.haulageJobs === 1 ? '' : 's'}</span>}
            <span className="ph-lock">
              <span className="material-symbols-rounded">{data.locked ? 'lock' : 'lock_open'}</span>
              {data.locked ? 'Locked' : 'Unlocked'}
            </span>
          </div>

          <div className="ph-toolbar">
            <div className="content-tab-strip ph-tabs" role="tablist" aria-label="Plan history">
              {PLAN_HISTORY_TABS.map(t => (
                <button
                  key={t.key}
                  id={`ph-tab-${t.key}`}
                  type="button"
                  role="tab"
                  data-tab={t.key}
                  aria-selected={tab === t.key}
                  aria-controls="ph-tabpanel"
                  tabIndex={tab === t.key ? 0 : -1}
                  className={`content-tab ph-tab${tab === t.key ? ' active' : ''}`}
                  onClick={() => setTab(t.key)}
                  onKeyDown={onTabKey}
                >
                  <span className="ph-tab-label">{t.label}</span>
                  <span className="ph-tab-count">{counts[t.key]}</span>
                  {t.key === 'email' && emailProblems > 0 && <Warn count={emailProblems} where="for this day" />}
                </button>
              ))}
            </div>
            <div className="filter-group ph-search">
              <label htmlFor="ph-search-input">
                Search {tabInfo.label.toLowerCase()}
                <SearchHelp fields={['order / PO no.', 'user', 'haulier', 'driver', 'vehicle', 'product', 'email subject', 'recipient']} />
              </label>
              <Input
                id="ph-search-input"
                type="text"
                placeholder="Order, user, haulier, recipient..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon="search"
              />
            </div>
          </div>

          <div id="ph-tabpanel" role="tabpanel" aria-labelledby={`ph-tab-${tab}`} className="ph-panel">
            {days.length === 0 && (
              <p className="ph-empty">
                {searching
                  ? `Nothing in ${tabInfo.label} matches this search.`
                  : `No ${tabInfo.label.toLowerCase()} recorded for this day yet.`}
              </p>
            )}

            {days.map(day => {
              const dayKey = `${tab}|${day.day}`;
              const open = isDayOpen({ isToday: day.day === today, searching, flipped: flippedDays.has(dayKey) });
              return (
                <section key={dayKey} className={`ph-day${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="ph-day-head"
                    aria-expanded={open}
                    onClick={() => flip(setFlippedDays, dayKey)}
                  >
                    <span className="material-symbols-rounded ph-chevron" aria-hidden="true">chevron_right</span>
                    <span className="ph-day-label">{day.label}</span>
                    {day.day === today && <span className="ph-today">Today</span>}
                    <span className="ph-day-count">{countWords(day.count, tab)}</span>
                    {day.problem && <Warn count={day.problems} where="that day" />}
                  </button>
                  {open && (
                    <div className="ph-subgroups">
                      {day.subgroups.map(sub => {
                        const subKey = `${tab}|${sub.key}`;
                        const subOpen = isSubgroupOpen({ searching, flipped: flippedSubs.has(subKey) });
                        const records = subgroupRecords(sub);
                        return (
                          <div key={subKey} className={`ph-sub${subOpen ? ' open' : ''}${sub.problem ? ' has-problem' : ''}`}>
                            <button
                              type="button"
                              className="ph-sub-head"
                              aria-expanded={subOpen}
                              onClick={() => flip(setFlippedSubs, subKey)}
                            >
                              <span className="material-symbols-rounded ph-chevron" aria-hidden="true">chevron_right</span>
                              <span className="ph-sub-what">
                                <span className="ph-sub-label">
                                  {subgroupLabel(sub, date)} <span className="ph-sub-time">({timeOf(sub.startAt)})</span>
                                </span>
                                {records && <span className="ph-sub-records">{records}</span>}
                              </span>
                              <span className="ph-sub-flag">
                                {sub.problem && <Warn count={sub.problems} where="in this send" />}
                              </span>
                              <span className="ph-sub-count">{countWords(sub.count, tab)}</span>
                              <span className="ph-sub-user">
                                <span className="material-symbols-rounded" aria-hidden="true">person</span>
                                {sub.users.join(', ') || '—'}
                              </span>
                            </button>
                            {subOpen && (
                              <div className="history-timeline ph-sub-body">
                                {sub.events.map((ev, i) => renderEntry(ev, i, sub.events))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      <SentEmailModal
        isOpen={viewEmail != null}
        onClose={() => setViewEmail(null)}
        emailId={viewEmail?.emailId}
        changeLogId={viewEmail?.changeLogId}
      />
    </Modal>
  );
}

export default PlanHistoryModal;
